/**
 * WebLogger - 基于 IndexedDB 的 Web 日志记录器
 * 提供日志记录、存储、导出和自动清理功能
 */
import dayjs from 'dayjs'
class WebLogger {
  /**
   * 构造函数
   * @param {string} databaseName - IndexedDB 数据库名称
   * @param {string} objectStoreName - 对象存储名称
   * @param {number} maxLogEntries - 最大日志条数，超过此数量将触发清理
   * @param {number} trimBatchSize - 每次清理的批次大小
   */
  constructor(
    databaseName = 'WebLogDB',
    objectStoreName = 'logs',
    maxLogEntries = 50000,
    trimBatchSize = 10000
  ) {
    this.databaseName = databaseName
    this.objectStoreName = objectStoreName
    this.maxLogEntries = maxLogEntries
    this.trimBatchSize = trimBatchSize
    this.database = null
    this.isInitialized = false
    this._initializeDatabase()
  }

  /**
   * 初始化 IndexedDB 数据库
   * 创建数据库连接和对象存储，并在初始化完成后检查日志数量
   * @private
   */
  _initializeDatabase() {
    const request = indexedDB.open(this.databaseName, 1)

    // 数据库版本升级时创建对象存储
    request.onupgradeneeded = (event) => {
      const database = event.target.result
      if (!database.objectStoreNames.contains(this.objectStoreName)) {
        database.createObjectStore(this.objectStoreName, { keyPath: 'id', autoIncrement: true })
      }
    }

    // 数据库打开成功
    request.onsuccess = (event) => {
      this.database = event.target.result
      this.isInitialized = true
      // 初始化时检查一次日志数量，避免数据库过大
      this._enforceMaxSize()
    }

    // 数据库打开失败
    request.onerror = (event) => {
      console.error('WebLogger DB init failed:', event)
    }
  }

  /**
   * 添加日志条目到数据库
   * @param {string} level - 日志级别 ('log', 'warn', 'error')
   * @param {Array} logArguments - 日志参数数组
   * @private
   */
  _appendLogEntry(level, logArguments) {
    // 确保数据库已初始化
    if (!this.database || !this.isInitialized) return

    const transaction = this.database.transaction([this.objectStoreName], 'readwrite')
    const objectStore = transaction.objectStore(this.objectStoreName)

    // 构建日志条目对象
    const logEntry = {
      timestamp: dayjs().format('YYYY-MM-DD HH:mm:ss:SSS'),
      level,
      message: logArguments
        .map((argumentItem) => (typeof argumentItem === 'object' ? JSON.stringify(argumentItem) : argumentItem))
        .join(' ')
    }

    // 存储到 IndexedDB
    objectStore.add(logEntry)

    // 同时输出到浏览器控制台，便于开发调试
    console[level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log'](
      `[WebLog ${level.toUpperCase()}]`,
      ...logArguments
    )
  }

  /**
   * 记录普通日志
   * @param {...any} args - 日志参数
   */
  log(...args) {
    this._appendLogEntry('log', args)
  }

  /**
   * 记录错误日志
   * @param {...any} args - 日志参数
   */
  error(...args) {
    this._appendLogEntry('error', args)
  }

  /**
   * 记录警告日志
   * @param {...any} args - 日志参数
   */
  warn(...args) {
    this._appendLogEntry('warn', args)
  }

  /**
   * 检查并强制执行最大日志数量限制
   * 在初始化时调用一次，避免数据库过大
   * @private
   */
  _enforceMaxSize() {
    if (!this.database) return

    const transaction = this.database.transaction([this.objectStoreName], 'readonly')
    const objectStore = transaction.objectStore(this.objectStoreName)
    const countRequest = objectStore.count()

    countRequest.onsuccess = () => {
      const totalCount = countRequest.result
      // 如果超过最大限制，删除最旧的日志
      if (totalCount > this.maxLogEntries) {
        this._deleteOldestLogs(totalCount - this.maxLogEntries + this.trimBatchSize)
      }
    }

    countRequest.onerror = (event) => {
      console.error('WebLogger count check failed:', event)
    }
  }

  /**
   * 删除最旧的日志条目
   * 使用游标遍历，从最早的记录开始删除
   * @param {number} deleteCount - 需要删除的日志条数
   * @private
   */
  _deleteOldestLogs(deleteCount) {
    const transaction = this.database.transaction([this.objectStoreName], 'readwrite')
    const objectStore = transaction.objectStore(this.objectStoreName)

    let deletedCount = 0
    const cursorRequest = objectStore.openCursor()

    cursorRequest.onsuccess = (event) => {
      const cursor = event.target.result
      // 删除指定数量的最旧记录
      if (cursor && deletedCount < deleteCount) {
        objectStore.delete(cursor.primaryKey)
        deletedCount++
        cursor.continue()
      }
    }
  }

  /**
   * 导出所有日志到文件
   * 将 IndexedDB 中的所有日志导出为 .log 文件并自动下载
   * @returns {Promise} 导出操作的 Promise
   */
  exportLogs() {
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction([this.objectStoreName], 'readonly')
      const objectStore = transaction.objectStore(this.objectStoreName)
      const cursorRequest = objectStore.openCursor()
      const logs = []

      cursorRequest.onsuccess = (event) => {
        const cursor = event.target.result
        if (cursor) {
          const log = cursor.value
          // 格式化日志条目为可读格式
          logs.push(`[${log.timestamp}] [${log.level.toUpperCase()}] ${log.message}`)
          cursor.continue()
        } else {
          // 所有日志读取完成，创建下载文件
          const blob = new Blob([logs.join('\n')], { type: 'text/plain' })
          const url = URL.createObjectURL(blob)
          const downloadLink = document.createElement('a')
          downloadLink.href = url
          downloadLink.download = `Job_${Date.now()}.log`
          downloadLink.click()
          URL.revokeObjectURL(url)
          resolve()
        }
      }

      cursorRequest.onerror = reject
    })
  }
}

// 初始化并暴露全局方法
window._weblog = new WebLogger()

/**
 * 使用示例：
 * 
 * // 记录不同类型的日志
 * _weblog.log('Hello world')
 * _weblog.error('Something went wrong', { code: 500 })
 * _weblog.warn('This is a warning!')
 * 
 * // 导出所有日志到文件
 * _weblog.exportLogs()
 */
